import React, { createContext, useContext, useEffect, useMemo, useState } from 'react';
import { useGetIdentity } from '@refinedev/core';
import type { OtdUserSession } from '@/providers/authProvider';
import { getCurrentSessionMarker } from '@/providers/authProvider';

export interface SessionData {
  sessionId: string;
  timeoutAt?: string;
  roles: string[];
  isAuthenticated: boolean;
  userId: string;
}

interface SessionContextValue {
  sessionData: SessionData;
  setSessionData: (sessionData: SessionData) => void;
  clearSession: () => void;
  isLoading: boolean;
}

const defaultSessionData: SessionData = {
  sessionId: '',
  timeoutAt: undefined,
  roles: [],
  isAuthenticated: false,
  userId: '',
};

const SessionContext = createContext<SessionContextValue>({
  sessionData: defaultSessionData,
  setSessionData: () => {},
  clearSession: () => {},
  isLoading: true,
});

export const SessionProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { data: identity, isLoading } = useGetIdentity<OtdUserSession>();
  const [sessionData, setSessionData] = useState<SessionData>(defaultSessionData);

  useEffect(() => {
    if (!identity?.userId) {
      setSessionData(defaultSessionData);
      return;
    }

    const roleList = identity.role
      ? identity.role.split(',').map((role) => role.trim()).filter(Boolean)
      : [];

    setSessionData({
      sessionId: getCurrentSessionMarker(),
      timeoutAt: identity.expiresOn && identity.expiresOn !== 'never' ? identity.expiresOn : undefined,
      roles: roleList,
      isAuthenticated: true,
      userId: identity.userId,
    });
  }, [identity]);

  const clearSession = () => setSessionData(defaultSessionData);

  const value = useMemo(
    () => ({
      sessionData,
      setSessionData,
      clearSession,
      isLoading,
    }),
    [sessionData, isLoading],
  );

  return <SessionContext.Provider value={value}>{children}</SessionContext.Provider>;
};

export const useSession = () => useContext(SessionContext);
